import { useState, useEffect } from "react";
import { Button, Table, Modal, Form, Dropdown } from "react-bootstrap";
import DashboardLayout from "../components/layout/DashboardLayout";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import { PropagateLoader } from "react-spinners";
import { useSearchParams } from "react-router-dom";

import { FaCogs, FaPlus, FaEdit, FaTrash, FaDownload } from "react-icons/fa";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useTranslate } from "../hooks/LanguageProvider";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface Machine {
  id_machine: number;
  machine_name: string;
  machine_type: string;
  manufacturer: string;
  capacity: string;
  quantity: number;
}

export default function SupplierMachines() {
  const { translate } = useTranslate();
const t = translate;
  const [searchParams] = useSearchParams();
  const id_user =
    searchParams.get("id_user") ?? localStorage.getItem("userid") ?? "";

  const [machines, setMachines] = useState<Machine[]>([]);
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("machine_name");

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [form, setForm] = useState({
    machine_name: "",
    machine_type: "",
    manufacturer: "",
    capacity: "",
    quantity: "",
  });

  const [loading, setLoading] = useState(false);
  const [colum, setSortColum] = useState("machine_name");
  const [sort, setSort] = useState("ASC");

  const [selectedColumns, setSelectedColumns] = useState({
    machine_name: true,
    machine_type: true,
    manufacturer: true,
    capacity: true,
    quantity: true,
  });

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  /* ================= LOAD ================= */

  async function loadMachines() {
    try {
      const res = await fetch(`${backendUrl}/api/supplier-machines/${id_user}`);
      const data = await res.json();
      setMachines(data);
    } catch {
      toast.error(t("error_loading_machines"));
    }
  }

  useEffect(() => {
    loadMachines();
  }, []);

  /* ================= SEARCH ================= */

  const filtered = machines.filter((m) => {
    const value = (m as any)[searchField]?.toString().toLowerCase() || "";
    return value.includes(search.toLowerCase());
  });

  /* ================= PAGINATION ================= */

  const pageCount = Math.ceil(filtered.length / limit);
  const offset = page * limit;
  const current = filtered.slice(offset, offset + limit);

  /* ================= SORT (LIKE PRODUCTS) ================= */

  const handleSortingColum = async (column: string) => {
    if (loading) return;

    const newSort = sort === "ASC" ? "DESC" : "ASC";

    setSortColum(column);
    setSort(newSort);

    setLoading(true);

    try {
      const res = await fetch(`${backendUrl}/api/supplier-machines/${id_user}`);
      const data = await res.json();

      // apply sorting AFTER fetch (same UX as role)
      const sorted = [...data].sort((a: any, b: any) => {
        if (newSort === "ASC") return a[column] > b[column] ? 1 : -1;
        return a[column] < b[column] ? 1 : -1;
      });

      setMachines(sorted);
    } catch (error) {
      console.error(error);
      toast.error(t("error_sorting"));
    } finally {
      setLoading(false);
    }
  };

  /* ================= SELECT ================= */

  const handleSelectAll = () => {
    if (selectedRows.length === current.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(current.map((m) => m.id_machine));
    }
  };

  const handleSelectRow = (id: number) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((i) => i !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  /* ================= COLUMN TOGGLE ================= */

  const handleColumnChange = (column: string) => {
    setSelectedColumns((prev: any) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  /* ================= EXPORT ================= */

  const selectedData = machines.filter((m) =>
    selectedRows.includes(m.id_machine),
  );

  function exportExcel() {
    if (selectedRows.length === 0) {
      toast.warning(t("select_one_machine"));
      return;
    }

    const ws = XLSX.utils.json_to_sheet(selectedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Machines");

    XLSX.writeFile(wb, "machines_selected.xlsx");
  }

  function exportCSV() {
    if (selectedRows.length === 0) {
      toast.warning(t("select_one_machine"));
      return;
    }

    const ws = XLSX.utils.json_to_sheet(selectedData);
    const csv = XLSX.utils.sheet_to_csv(ws);

    const blob = new Blob([csv]);
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "machines_selected.csv";
    a.click();
  }

  function exportPDF() {
    if (selectedRows.length === 0) {
      toast.warning(t("select_one_machine"));
      return;
    }

    const doc = new jsPDF();

    autoTable(doc, {
      head: [["Machine", "Type", "Manufacturer", "Capacity", "Quantity"]],
      body: selectedData.map((m) => [
        m.machine_name,
        m.machine_type,
        m.manufacturer,
        m.capacity,
        m.quantity,
      ]),
    });

    doc.save("machines_selected.pdf");
  }

  /* ================= CRUD ================= */

  function resetForm() {
    setEditId(null);
    setForm({
      machine_name: "",
      machine_type: "",
      manufacturer: "",
      capacity: "",
      quantity: "",
    });
    setShowModal(false);
  }

  async function addMachine() {
    try {
      const res = await fetch(`${backendUrl}/api/supplier-machines/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_user,
          machine_name: form.machine_name,
          machine_type: form.machine_type,
          manufacturer: form.manufacturer,
          capacity: form.capacity,
          quantity: Number(form.quantity),
        }),
      });

      if (!res.ok) throw new Error("Failed to add");

      toast.success(t("machine_added"));

      resetForm();
      loadMachines();
    } catch (error) {
      console.error(error);
      toast.error(t("error_adding_machine"));
    }
  }

  async function updateMachine() {
    if (!editId) {
      toast.error(t("no_machine_selected"));
      return;
    }

    try {
      const res = await fetch(
        `${backendUrl}/api/supplier-machines/update/${editId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            machine_name: form.machine_name,
            machine_type: form.machine_type,
            manufacturer: form.manufacturer,
            capacity: form.capacity,
            quantity: Number(form.quantity),
          }),
        },
      );

      if (!res.ok) throw new Error("Failed to update");

      toast.success(t("machine_updated"));

      resetForm();
      loadMachines();
    } catch (error) {
      console.error(error);
      toast.error(t("error_updating_machine"));
    }
  }

  async function deleteMachine(id: number) {
    if (!window.confirm(t("delete_machine_confirm"))) return;

    try {
      const res = await fetch(`${backendUrl}/api/supplier-machines/${id}`, {
        method: "DELETE",
      });

      if (!res.ok) throw new Error("Failed to delete");

      toast.success(t("machine_deleted"));

      loadMachines();
    } catch (error) {
      console.error(error);
      toast.error(t("error_deleting_machine"));
    }
  }
  /* ================= UI ================= */

  return (
    <div className="container-fluid">
      {/* HEADER */}
      <div className="row mb-3">
        <div className="col-md-6">
          <h4>
            <FaCogs className="me-2" />
            {t("machines")} ({machines.length})
          </h4>
        </div>

        <div className="col-md-6 text-right">
          <Dropdown className="d-inline me-2">
            <Dropdown.Toggle variant="warning">
              <FaDownload /> {t("export")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={exportExcel}>Excel</Dropdown.Item>
              <Dropdown.Item onClick={exportCSV}>CSV</Dropdown.Item>
              <Dropdown.Item onClick={exportPDF}>PDF</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Button variant="warning" onClick={() => setShowModal(true)}>
            <FaPlus /> {t("add_machine")}
          </Button>
        </div>
      </div>

      {/* SEARCH + CONTROLS */}
      <div className="row mb-3">
        <div className="col-md-4">
          <div className="input-group">
            <Dropdown>
              <Dropdown.Toggle variant="link">
                <i className="fas fa-chevron-down"></i>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setSearchField("machine_name")}>
                  {t("machine")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSearchField("machine_type")}>
                  {t("type")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSearchField("manufacturer")}>
                  {t("manufacturer")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <input
              type="text"
              className="form-control"
              placeholder={`${t("search_by")} ${t(searchField)}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <Button variant="secondary" onClick={() => setSearch("")}>
              ✕
            </Button>
          </div>
        </div>

        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length d-flex align-items-center">
            <label style={{ marginBottom: "0" }}>
              {t("show")}
              <select
                className="custom-select custom-select-sm form-control form-control-sm ms-2"
                style={{ width: "70px" }}
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </label>
          </div>

          <Dropdown>
            <Dropdown.Toggle variant="">
              <i className="las la-eye"></i>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {Object.keys(selectedColumns).map((col) => (
                <Dropdown.Item key={col}>
                  <input
                    type="checkbox"
                    checked={
                      selectedColumns[col as keyof typeof selectedColumns]
                    }
                    onChange={() => handleColumnChange(col)}
                  />{" "}
                  {t(col)}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* TABLE */}
      <Table className="dataTable role-table">
        <thead>
          <tr className="ligth ligth-data">
            <th style={{ width: "5%" }}>
              <input
                type="checkbox"
                checked={selectedRows.length === current.length}
                onChange={handleSelectAll}
              />
            </th>

            {selectedColumns.machine_name && (
              <th
                className="sorting"
                onClick={() => handleSortingColum("machine_name")}
              >
                {t("machine")}
              </th>
            )}

            {selectedColumns.machine_type && (
              <th
                className="sorting"
                onClick={() => handleSortingColum("machine_type")}
              >
                {t("type")}
              </th>
            )}

            {selectedColumns.manufacturer && (
              <th
                className="sorting"
                onClick={() => handleSortingColum("manufacturer")}
              >
                {t("manufacturer")}
              </th>
            )}

            {selectedColumns.quantity && (
              <th
                className="sorting"
                onClick={() => handleSortingColum("capacity")}
              >
                {t("capacity")}
              </th>
            )}
            {selectedColumns.quantity && (
              <th
                className="sorting"
                onClick={() => handleSortingColum("quantity")}
              >
                {t("quantity")}
              </th>
            )}

            <th className="text-center">{t("actions")}</th>
          </tr>
        </thead>

       <tbody className="ligth-body">
  {loading ? (
    <tr>
      <td colSpan={7} className="text-center">
        <PropagateLoader color="#123abc" />
      </td>
    </tr>
  ) : current.length > 0 ? (
    current.map((m) => (
      <tr key={m.id_machine}>
        <td>
          <input
            type="checkbox"
            checked={selectedRows.includes(m.id_machine)}
            onChange={() => handleSelectRow(m.id_machine)}
          />
        </td>

        {selectedColumns.machine_name && <td>{m.machine_name}</td>}
        {selectedColumns.machine_type && <td>{m.machine_type}</td>}
        {selectedColumns.manufacturer && <td>{m.manufacturer}</td>}
        {selectedColumns.capacity && <td>{m.capacity}</td>}
        {selectedColumns.quantity && <td>{m.quantity}</td>}

        <td className="text-center">
          <Button
            size="sm"
            variant="warning"
            className="me-2 rounded-circle"
            onClick={() => {
              setEditId(m.id_machine);
              setForm({
                machine_name: m.machine_name,
                machine_type: m.machine_type,
                manufacturer: m.manufacturer,
                capacity: m.capacity,
                quantity: String(m.quantity),
              });
              setShowModal(true);
            }}
          >
            <FaEdit />
          </Button>

          <Button
            size="sm"
            variant="danger"
            className="rounded-circle"
            onClick={() => deleteMachine(m.id_machine)}
          >
            <FaTrash />
          </Button>
        </td>
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan={7} style={{ textAlign: "center" }}>
        {t("no_machines")}
      </td>
    </tr>
  )}
</tbody>
      </Table>

      {/* PAGINATION */}
      <div className="d-flex justify-content-between mt-3">
        <span>
          {t("displaying")} {current.length} {t("out_of")} {filtered.length}
        </span>

        <ReactPaginate
          previousLabel={t("previous")}
          nextLabel={t("next")}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={(d) => setPage(d.selected)}
          containerClassName={"pagination justify-content-end"}
          pageClassName={"page-item"}
          pageLinkClassName={"page-link"}
          previousClassName={"page-item"}
          previousLinkClassName={"page-link"}
          nextClassName={"page-item"}
          nextLinkClassName={"page-link"}
          activeClassName={"active"}
          forcePage={page}
        />
      </div>

      {/* MODAL */}
      <Modal show={showModal} onHide={resetForm}>
        <Modal.Header closeButton>
          <Modal.Title>{editId ? t("edit_machine") : t("add_machine")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("machine_name")}</Form.Label>
              <Form.Control
                value={form.machine_name}
                onChange={(e) =>
                  setForm({ ...form, machine_name: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("type")}</Form.Label>
              <Form.Control
                value={form.machine_type}
                onChange={(e) =>
                  setForm({ ...form, machine_type: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("manufacturer")}</Form.Label>
              <Form.Control
                value={form.manufacturer}
                onChange={(e) =>
                  setForm({ ...form, manufacturer: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("capacity")}</Form.Label>
              <Form.Control
                value={form.capacity}
                onChange={(e) => setForm({ ...form, capacity: e.target.value })}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>{t("quantity")}</Form.Label>
              <Form.Control
                type="number"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={resetForm}>
            {t("cancel")}
          </Button>

          <Button
            variant="warning"
            onClick={editId ? updateMachine : addMachine}
          >
            {t("save")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
